'use client';

import React, { useEffect } from 'react';

import { Entry } from 'contentful';
import { Document } from '@contentful/rich-text-types';

import classNames from 'classnames';

import Slider, { Settings } from 'react-slick';

import { TypeModularProductCardFields } from '@/core/generated-types';

import {
  Badge,
  InternalLink,
  LuvImage,
  RichText,
  UniversalCta,
  DynamicInfoModal,
} from '@/components';

import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';
import { slugBuilder } from '@/core/contentfulConfig';

import { ExtendedProductFields } from '@/core/contentfulConfig';

import useLocale from '@/hooks/useLocale';

import { ShopifyProduct } from '@/lib/shopify';

import { formatMoney } from '@/utils/helpers';
import {
  PRODUCT_EVENT_TYPES,
  ProductEventPayload,
  trackProductEvent,
} from '@/utils/rudderstack';

import { GenericText } from '../GenericText';

import { usePathname } from 'next/navigation';

import {
  LetterSpacing,
  TextSize,
  TextTransform,
  TextWeight,
} from '../GenericText/GenericText.types';

export enum ProductCardStyleEnum {
  Default = 'default',
  SortAndFilter = 'sortAndFilter',
}

interface ModularProductCardProps
  extends Omit<TypeModularProductCardFields, 'product'> {
  product?: Entry<ExtendedProductFields>;
  classes?: string;
  ProductCardStyle?: ProductCardStyleEnum;
  disableCarousel?: boolean;
}

const SESSION_STORAGE_KEY = 'ModularProductCardScrollPosition';

function ModularProductCard(fields: ModularProductCardProps) {
  const {
    leadHeading,
    leadParagraphText,
    title,
    images,
    badge,
    description,
    listHeading,
    listItems,
    overlineText,
    style,
    overlineTextStyle,
    textLink,
    productLink,
    trailingText,
    trailingParagraphText,
    classes,
    product,
    dynamicInfoModal,
    hoverEffect,
    overlineTextCustomSize,
    overlineTextMobileCustomSize,
    titleMobileCustomSize,
    queryString,
    trailingTextSize,
    trailingTextWeight,
    trailingTextStyle,
    ProductCardStyle = ProductCardStyleEnum.Default,
    disableCarousel,
  } = fields;

  const isDefaultProductCardStyle =
    ProductCardStyle === ProductCardStyleEnum.Default;

  const shopifyProduct =
    product?.fields?.shopifyProduct || ({} as ShopifyProduct);

  const { price, strikethroughPrice } = product?.fields || {};

  const locale = useLocale();

  const pathname = usePathname();

  useEffect(() => {
    const sessionStorageKey = `${SESSION_STORAGE_KEY}[${window.location.pathname}]`;

    const scrollPosition = sessionStorage.getItem(sessionStorageKey);

    if (scrollPosition) {
      window.scrollTo(0, +scrollPosition);

      sessionStorage.removeItem(sessionStorageKey);
    }
  }, []);

  const handleCardClick = () => {
    const payload = {
      url: pathname || '',
      destinationUrl: slugBuilder(productLink) || '',
      productId: shopifyProduct?.id || '',
      name: product?.fields?.title,
      category: shopifyProduct?.productType || '',
      sku: shopifyProduct?.variants?.[0]?.sku || '',
      ageRange: product?.fields?.ageRange || '',
      price: shopifyProduct?.variants?.[0].price?.amount || 0,
      quantity: 1,
    };

    if (payload.name) {
      trackProductEvent(
        PRODUCT_EVENT_TYPES.PRODUCT_CLICKED,
        payload as ProductEventPayload,
      );
    }

    sessionStorage.setItem(
      `${SESSION_STORAGE_KEY}[${window.location.pathname}]`,
      window.scrollY.toString(),
    );
  };

  const LeadingBlock = () => (
    <>
      {leadHeading && (
        <Heading text={leadHeading} tag={HeadingTag.H3} size={HeadingSize.H3} />
      )}

      {leadParagraphText && (
        <p className="text-base mb-4 md:mb-6">{leadParagraphText}</p>
      )}
    </>
  );

  const CarouselBlock = () => {
    const baseSettings: Settings = {
      dots: true,
      arrows: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      speed: 500,
    };

    const hasMultipleImages = !disableCarousel && images.length > 1;

    if (hasMultipleImages) {
      return (
        <div
          data-testid="modularProductCard-carousel"
          className="grid grid-cols-1 luv-carousel product-slider modular-product-slider"
        >
          <Slider {...baseSettings}>
            {images?.map((image, index) => (
              <LuvImage
                key={`${image.sys.id}-${index}`}
                imageData={image}
                alt={image?.fields?.altText}
              />
            ))}
          </Slider>
        </div>
      );
    }

    const featuredImage = images?.[0];

    return (
      <LuvImage
        testId="modularProductCard-featuredImage"
        imageData={featuredImage}
        alt={featuredImage?.fields?.altText}
        className="w-full"
        width={480}
        height={480}
        quality={85}
        style={{
          aspectRatio: '4 / 3',
          objectFit: 'contain',
        }}
      />
    );
  };

  const ProductDetailBlock = () => {
    const areCoreElementsPresent = title;

    const trailingTextClasses = classNames('text-base', {
      [`font-${trailingTextWeight}`]: trailingTextWeight,
      [`lg:text-${trailingTextSize}`]: trailingTextSize,
      'font-bradford': trailingTextStyle !== 'regular',
    });

    return (
      <div
        className={classNames({
          'px-4 md:px-6 pb-4 md:pb-6':
            style === 'partiallyContained' && areCoreElementsPresent,
        })}
      >
        <div
          className={classNames({
            'md:mt-[18px] md:mb-4': areCoreElementsPresent,
          })}
        >
          {badge && <Badge {...badge.fields} />}

          {overlineText && (
            <GenericText
              as="p"
              className={classNames('mt-1 md:mb-1', {
                'font-bradford': overlineTextStyle === 'italics',
              })}
              size={TextSize.SM}
              weight={
                overlineTextStyle === 'caps'
                  ? TextWeight.BOLD
                  : TextWeight.NORMAL
              }
              transform={
                overlineTextStyle === 'caps'
                  ? TextTransform.UPPERCASE
                  : TextTransform.NORMAL
              }
              letterSpacing={
                overlineTextStyle === 'caps' ? LetterSpacing.WIDER : undefined
              }
              customSize={overlineTextCustomSize}
              mobileCustomSize={overlineTextMobileCustomSize}
            >
              {overlineText}
            </GenericText>
          )}

          {title && (
            <GenericText
              as="h2"
              size={TextSize.H2}
              className={classNames('font-semibold md:mb-2', {
                'hover:text-blue-deep-hover hover:underline': !hoverEffect,
              })}
              weight={TextWeight.SEMIBOLD}
              mobileCustomSize={titleMobileCustomSize}
            >
              {title}
            </GenericText>
          )}

          {description && isDefaultProductCardStyle && (
            <RichText
              content={description as Document}
              addParagraphMargins={false}
              classes="text-sm md:text-base"
            />
          )}

          {trailingText && (
            <p className={trailingTextClasses}>{trailingText}</p>
          )}
        </div>

        {listHeading && (
          <Heading
            text={listHeading}
            tag={HeadingTag.H4}
            size={HeadingSize.H4}
            classes="mb-1"
          />
        )}

        {listItems && <RichText content={listItems as Document} />}

        <div className="flex gap-2 items-center">
          {!!price && !isNaN(price) && (
            <p className="font-bold">{formatMoney(Number(price), locale)}</p>
          )}

          {!!strikethroughPrice && !isNaN(strikethroughPrice) && (
            <p className="line-through font-bold text-grey-light">
              {formatMoney(Number(strikethroughPrice), locale)}
            </p>
          )}
        </div>

        {textLink && (
          <UniversalCta
            link={textLink}
            defaultButtonStyle="Link"
            className="no-underline hover:text-blue-deep-hover mt-2 md:mt-4"
          />
        )}

        {trailingParagraphText && (
          <div className="mt-2 md:mt-3">
            <RichText content={trailingParagraphText as Document} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <InternalLink
        link={productLink}
        onClick={handleCardClick}
        classes="inline-block product-card"
        hideArrow
        queryString={queryString}
      >
        <div
          data-testid="modularProductCard"
          className={classNames(
            'lg:max-w-[360px] text-left grid gap-y-2 lg:gap-y-6',
            classes,
            {
              'shadow-lg rounded-md p-4 md:p-6 bg-white':
                style === 'fullyContained',
              'hover:!text-blue-deep': hoverEffect,
            },
          )}
        >
          {isDefaultProductCardStyle && <LeadingBlock />}

          <div
            className={classNames({
              'shadow-lg rounded-md overflow-hidden bg-white':
                style === 'partiallyContained',
            })}
          >
            <CarouselBlock />

            <ProductDetailBlock />
          </div>
        </div>
      </InternalLink>

      {isDefaultProductCardStyle && (
        <DynamicInfoModal dynamicInfoModal={dynamicInfoModal} />
      )}
    </div>
  );
}

export default ModularProductCard;
